import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceRespSchema,
  InvoiceSummaryResp,
  InvoiceVoidReasonCodeEnum,
  VoidInvoiceErrorResponse,
  VoidSingleInvoiceFormInput,
  VoidSingleInvoiceRequest,
  VoidSingleInvoiceRequestSchema,
} from '@monetize/types/app';
import { isBillingPlatformApiErrorResponse } from '@monetize/types/common';
import { isAxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../api/axios';
import { useVoidInvoice } from '../../api/invoiceService';
import {
  MAlert,
  MButton,
  MCenterModal,
  MFormField,
  MStack,
  MText,
} from '../../components/Monetize';
import { MCustomSelect } from '../../components/Monetize/MCustomSelect/MCustomSelect';
import { MTextAreaCharacterLimited } from '../../components/Monetize/MTextAreaCharacterLimited';
import { INVOICE_VOID_REASON_CODE_OPTIONS } from '../../constants/invoice';
import { useToast } from '../../services/toast';

interface VoidInvoiceModalProps {
  invoice: IInvoiceRespSchema | InvoiceSummaryResp;
  isOpen: boolean;
  onClose: () => void;
  refreshData: () => void;
}

/**
 * The void endpoint rejects the whole request with a 400 when the invoice
 * isn't voidable, carrying the specific reason in its error context instead
 * of a generic message.
 */
function getVoidInvoiceErrorMessage(err: unknown): string | undefined {
  const response = isAxiosError(err) ? err.response?.data : err;
  if (
    isBillingPlatformApiErrorResponse(response) &&
    Array.isArray(response.error.context?.errors)
  ) {
    const [firstError] = response.error.context
      .errors as VoidInvoiceErrorResponse[];
    return firstError?.message;
  }
  return undefined;
}

export const VoidInvoiceModal = ({
  invoice,
  isOpen,
  onClose,
  refreshData,
}: VoidInvoiceModalProps) => {
  const { addToast } = useToast();
  const { mutateAsync: doVoidInvoice, isPending: isLoading } = useVoidInvoice();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<VoidSingleInvoiceFormInput, unknown, VoidSingleInvoiceRequest>({
    resolver: zodResolver(VoidSingleInvoiceRequestSchema),
    defaultValues: { internalComment: '' },
  });

  const reasonCode = watch('reasonCode');
  const internalComment = watch('internalComment');

  const isSubmitDisabled =
    !reasonCode ||
    (reasonCode === InvoiceVoidReasonCodeEnum.OTHER &&
      !internalComment?.trim());

  async function onSubmit(data: VoidSingleInvoiceRequest) {
    try {
      await doVoidInvoice({ invoiceId: invoice.id, data });
      addToast({
        summary: 'Success',
        detail: 'The invoice has been voided.',
        severity: 'success',
      });
      refreshData();
      onClose();
    } catch (err) {
      const invoiceErrorMessage = getVoidInvoiceErrorMessage(err);
      if (invoiceErrorMessage) {
        addToast({
          summary: 'Voiding Failed',
          detail: invoiceErrorMessage,
          severity: 'error',
        });
      } else {
        handleApiErrorToast(err);
      }
    }
  }

  const renderFooter = () => (
    <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
      <MButton
        variant="cancel"
        onClick={onClose}
        minW="auto"
        isDisabled={isLoading}
      >
        Cancel
      </MButton>
      <MButton
        form="void-invoice-form"
        type="submit"
        variant="primary"
        minW="auto"
        isDisabled={isSubmitDisabled}
        isLoading={isLoading}
      >
        Void Invoice
      </MButton>
    </MStack>
  );

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Void Invoice"
      size="md"
      showCloseButton={!isLoading}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={renderFooter}
    >
      <form id="void-invoice-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <MAlert type="warning" mb={4}>
          <MText>
            Voiding an invoice cannot be undone. Once an invoice has been
            voided, you may generate a new invoice for this period if needed.
          </MText>
        </MAlert>

        <MFormField
          isRequired
          label="Reason Code"
          error={errors.reasonCode}
          mb={3}
        >
          <Controller
            name="reasonCode"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                {...field}
                items={INVOICE_VOID_REASON_CODE_OPTIONS}
                placeholder="Select a reason"
                isDisabled={isLoading}
              />
            )}
          />
        </MFormField>

        <MFormField
          isRequired={reasonCode === InvoiceVoidReasonCodeEnum.OTHER}
          label="Internal Comment"
          tooltip="Include any additional explanation for why this invoice was voided. These comments are not exposed to end customers."
          error={errors.internalComment}
        >
          <Controller
            name="internalComment"
            control={control}
            render={({ field }) => (
              <MTextAreaCharacterLimited
                field={field}
                maxLength={500}
                placeholder="Describe the reason for voiding..."
                isDisabled={isLoading}
              />
            )}
          />
        </MFormField>
      </form>
    </MCenterModal>
  );
};
